
import { SidebarTrigger } from "@/components/ui/sidebar";
import { UserInfo } from "@/components/auth/UserInfo";
import { TopStatusBar } from "./TopStatusBar";
import { MobileStatusBar } from "./MobileStatusBar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Bell, Check, Plus, Minus, Megaphone, MessageCircle, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { useOptimizedNotifications } from "@/hooks/useOptimizedNotifications";
import { useUnreadAnnouncementsCount } from "@/hooks/useAnnouncements";
import { useUnreadMessagesCount } from "@/hooks/useChats";
import { FontSizeControl } from "@/components/FontSizeControl";
import { AnnouncementsList } from "./announcements/AnnouncementsList";
import { useNavigate } from "react-router-dom";
import { useRealtimeMessageNotifications } from "@/hooks/useRealtimeMessageNotifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AppHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    notifications, 
    unreadCount: unreadNotificationsCount,
    markAsRead,
    markAllAsRead,
    isMarkingAsRead,
    isMarkingAllAsRead
  } = useOptimizedNotifications();
  
  const { data: unreadAnnouncementsCount = 0 } = useUnreadAnnouncementsCount();
  const unreadMessagesCount = useUnreadMessagesCount();
  
  // Enable real-time message notifications
  useRealtimeMessageNotifications();

  const handleMarkAsRead = (notificationId: string) => {
    markAsRead({ notificationId });
  };

  const handleMarkAllAsRead = () => {
    if (user?.id) {
      markAllAsRead({ userId: user.id });
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="border-b bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80 sticky top-0 z-40 shadow-sm">
      <div className="flex items-center justify-between px-2 sm:px-3 md:px-4 lg:px-6 h-12 sm:h-14" dir="rtl">
        {/* Left Section - Desktop Sidebar Trigger */}
        <div className="flex items-center gap-2 sm:gap-3">
          <SidebarTrigger className="hidden lg:flex icon-hover hover:bg-accent rounded-md p-2 transition-colors" />
        </div>

        {/* Right Section - Enhanced for mobile */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Refresh Button */}
          <Button 
            variant="ghost" 
            size="icon"
            className="icon-hover rounded-full hover:bg-accent transition-colors h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10"
            onClick={handleRefresh}
            title="تحديث الصفحة"
          >
            <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
          </Button>
          
          {/* Font Size Control */}
          <FontSizeControl />
          
          {/* Language Switcher */}
          <LanguageSwitcher />
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Messages */}
          <Button 
            variant="ghost" 
            size="icon"
            className="relative icon-hover rounded-full hover:bg-accent transition-colors h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10"
            onClick={() => navigate('/messages')}
          >
            <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
            {unreadMessagesCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 flex items-center justify-center text-[9px] sm:text-xs animate-pulse-slow"
              >
                {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
              </Badge>
            )}
          </Button>
          
          {/* Announcements */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="relative icon-hover rounded-full hover:bg-accent transition-colors h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10"
              >
                <Megaphone className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                {unreadAnnouncementsCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 flex items-center justify-center text-[9px] sm:text-xs animate-pulse-slow"
                  >
                    {unreadAnnouncementsCount > 99 ? '99+' : unreadAnnouncementsCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-card/95 backdrop-blur-sm border shadow-xl"
              sideOffset={8}
            >
              <DropdownMenuLabel className="flex items-center justify-between p-4 border-b bg-muted/30">
                <span className="font-semibold text-card-foreground">الإعلانات</span>
              </DropdownMenuLabel>
              <div className="p-2 max-h-80 overflow-y-auto">
                <AnnouncementsList />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Notifications - Enhanced UI */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="relative icon-hover rounded-full hover:bg-accent transition-colors h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10"
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                {unreadNotificationsCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 flex items-center justify-center text-[9px] sm:text-xs animate-pulse-slow"
                  >
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className="w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-card/95 backdrop-blur-sm border shadow-xl"
              sideOffset={8}
            >
              <DropdownMenuLabel className="flex items-center justify-between p-4 border-b bg-muted/30">
                <span className="font-semibold text-card-foreground">الإشعارات</span>
                {unreadNotificationsCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAllAsRead}
                    className="text-xs hover:bg-accent text-primary h-7 px-2"
                  >
                    <Check className="w-3 h-3 ml-1" />
                    قراءة الكل
                  </Button>
                )}
              </DropdownMenuLabel>
              {notifications.length === 0 ? (
                <div className="p-6 text-center">
                  <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">لا توجد إشعارات</p>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {notifications.slice(0, 5).map((notification) => (
                    <DropdownMenuItem
                      key={notification.id}
                      className={`flex flex-col items-start p-4 cursor-pointer border-b last:border-b-0 hover:bg-accent/50 transition-colors ${
                        !notification.is_read ? 'bg-primary-light/50 border-l-4 border-l-primary' : ''
                      }`}
                      onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                      disabled={isMarkingAsRead}
                    >
                      <div className="font-medium text-sm text-card-foreground leading-relaxed">
                        {notification.message}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                        <span>{new Date(notification.created_at).toLocaleDateString('ar-SA')}</span>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Info */}
          <UserInfo />
        </div>
      </div>
      
      {/* Top Status Bar - Conditional Rendering */}
      <div className="top-status-bar-desktop">
        <TopStatusBar />
      </div>
      
      {/* Mobile Status Bar */}
      <MobileStatusBar />
    </div>
  );
}
